import { QueryAggregate } from '@powerportalspro/core';
import { useT } from '@powerportalspro/react';
import {
  ExportGridButton,
  GridToolbarItems,
  MainGrid,
  NavigateOpenRecordGridButton,
  type ViewDefinition,
} from '@powerportalspro/react-fluent';
import { useMemo } from 'react';

// The opportunities, with a totals row under the columns, which the view's columns set (each
// column's `aggregates`): how many there are, their estimated value in total and on average, their
// average probability, and the earliest and latest close dates. The figures are worked out by
// Dataverse over every opportunity the grid matches, not only the page shown, so a search changes
// them with the rows.
//
// Each figure has a format of its own: the estimated value in whole amounts, the probability to one
// decimal place. Each column's menu picks its own figures — a total, average, minimum, maximum or
// count, as its type takes — in place of the view's, and a number column's Totals menu sets their
// formats in place of the view's too. Export to Excel writes the totals as formulas under the rows.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.GridDemos.GridTotalsMainDemo';

const OPPORTUNITY_URL = 'demos/opportunity';

const OPPORTUNITIES_VIEW_ID = '3c8f1e52-7d4a-4b9e-a6c1-2f5d8e9b0a74';

const OPPORTUNITIES_FETCH_XML = `<fetch>
  <entity name='opportunity'>
    <attribute name='name' />
    <attribute name='parentaccountid' />
    <attribute name='estimatedvalue' />
    <attribute name='closeprobability' />
    <attribute name='estimatedclosedate' />
    <attribute name='statecode' />
    <order attribute='estimatedclosedate' descending='true' />
  </entity>
</fetch>`;


export function GridTotalsMainSample() {
  const t = useT();

  const views = useMemo<ViewDefinition[]>(
    () => [
      {
        id: OPPORTUNITIES_VIEW_ID,
        tableName: 'opportunity',
        displayName: t(`${STRINGS_BASE}.view-opportunities`),
        fetchXml: OPPORTUNITIES_FETCH_XML,
        columns: [
          // The figures in the totals row, under their columns. Money keeps the currency's symbol.
          { columnName: 'name', width: 260, aggregates: [{ aggregate: QueryAggregate.Count }] },
          { columnName: 'parentaccountid', width: 180 },
          {
            columnName: 'estimatedvalue',
            width: 160,
            aggregates: [
              { aggregate: QueryAggregate.Sum, format: 'N0' },
              { aggregate: QueryAggregate.Average, format: 'N0' },
            ],
          },
          { columnName: 'closeprobability', width: 120, aggregates: [{ aggregate: QueryAggregate.Average, format: 'N1' }] },
          {
            columnName: 'estimatedclosedate',
            width: 150,
            aggregates: [{ aggregate: QueryAggregate.Minimum }, { aggregate: QueryAggregate.Maximum }],
          },
          { columnName: 'statecode', width: 100 },
        ],
      },
    ],
    [t],
  );

  return (
    <MainGrid
      tableName="opportunity"
      minHeight="550px"
      maxHeight="550px"
      viewIds={[OPPORTUNITIES_VIEW_ID]}
      customViewDefinitions={views}
      defaultViewId={OPPORTUNITIES_VIEW_ID}
    >
      <GridToolbarItems>
        <NavigateOpenRecordGridButton urlFor={(record) => `${OPPORTUNITY_URL}?opportunityId=${record.id}`} />
        <ExportGridButton />
      </GridToolbarItems>
    </MainGrid>
  );
}
